import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DeliveryPoint,
  validateAddress,
  fetchServicePoints,
  fetchParcelBoxes,
  createDeliveryAddress,
  fetchShippingMethods,
  fetchSelectDeliveryMethod,
} from "../thunks/delivery.thunks";

type LoadingState = "idle" | "pending" | "fulfilled" | "rejected";

interface DeliveryState {
  address: {
    street: string;
    postalCode: string;
    city: string;
  };
  validateAddressResponse: { valid: boolean };
  validateAddressLoading: LoadingState;
  loading: LoadingState;
  error: string | null;
  servicePoints: DeliveryPoint[];
  parcelBoxes: DeliveryPoint[];
  selectedMethod: DeliveryMethodType;
  servicePointsLoading: LoadingState;
  selectedServicePoint: DeliveryPoint | null;
  selectedParcelBox: DeliveryPoint | null;
  parcelBoxesLoading: LoadingState;
  shippingMethods: {
    shipmentId: string;
    methods: ShippingMethod[];
  } | null;
  shippingMethodsLoading: LoadingState;
  createDeliveryAddressLoading: LoadingState;
  selectDeliveryMethodLoading: LoadingState;
  selectDeliveryMethodResponse: any;
}

interface ShippingMethod {
  id: string;
  name: string;
  price: string;
}

export type DeliveryMethodType = "servicePoint" | "parcelBox" | "pickup";

const initialState: DeliveryState = {
  address: {
    street: "",
    postalCode: "",
    city: "",
  },
  validateAddressLoading: "idle",
  validateAddressResponse: { valid: false },
  loading: "idle",
  error: null,
  servicePointsLoading: "idle",
  servicePoints: [],
  parcelBoxesLoading: "idle",
  parcelBoxes: [],
  selectedMethod: "servicePoint",
  selectedServicePoint: null,
  selectedParcelBox: null,
  shippingMethodsLoading: "idle",
  shippingMethods: null,
  createDeliveryAddressLoading: "idle",
  selectDeliveryMethodLoading: "idle",
  selectDeliveryMethodResponse: null,
};

const deliverySlice = createSlice({
  name: "delivery",
  initialState,
  reducers: {
    setDeliveryAddress: (
      state,
      action: PayloadAction<{
        street: string;
        postalCode: string;
        city: string;
      }>
    ) => {
      state.address = action.payload;
    },

    setSelectedDeliveryMethod: (
      state,
      action: PayloadAction<DeliveryMethodType>
    ) => {
      state.selectedMethod = action.payload;
    },

    setSelectedServicePoint: (
      state,
      action: PayloadAction<DeliveryPoint | null>
    ) => {
      state.selectedServicePoint = action.payload;
    },

    setSelectedParcelBox: (
      state,
      action: PayloadAction<DeliveryPoint | null>
    ) => {
      state.selectedParcelBox = action.payload;
    },

    resetDeliveryState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Validate Address
      .addCase(validateAddress.pending, (state) => {
        state.validateAddressLoading = "pending";
        state.error = null;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        state.validateAddressLoading = "fulfilled";
        state.validateAddressResponse = action.payload;
      })
      .addCase(validateAddress.rejected, (state, action) => {
        state.validateAddressLoading = "rejected";
        state.error = action.error.message || "Failed to validate address";
      })

      // Service Points
      .addCase(fetchServicePoints.pending, (state) => {
        state.servicePointsLoading = "pending";
        state.error = null;
      })
      .addCase(fetchServicePoints.fulfilled, (state, action) => {
        state.servicePointsLoading = "fulfilled";
        state.servicePoints = action.payload;
      })
      .addCase(fetchServicePoints.rejected, (state, action) => {
        state.servicePointsLoading = "rejected";
        state.error = action.error.message || "Failed to fetch service points";
      })

      // Parcel Boxes
      .addCase(fetchParcelBoxes.pending, (state) => {
        state.parcelBoxesLoading = "pending";
        state.error = null;
      })
      .addCase(fetchParcelBoxes.fulfilled, (state, action) => {
        state.parcelBoxesLoading = "fulfilled";
        state.parcelBoxes = action.payload;
      })
      .addCase(fetchParcelBoxes.rejected, (state, action) => {
        state.parcelBoxesLoading = "rejected";
        state.error = action.error.message || "Failed to fetch parcel boxes";
      })

      // Create Address
      .addCase(createDeliveryAddress.pending, (state) => {
        state.createDeliveryAddressLoading = "pending";
      })
      .addCase(createDeliveryAddress.fulfilled, (state) => {
        state.createDeliveryAddressLoading = "fulfilled";
      })
      .addCase(createDeliveryAddress.rejected, (state, action) => {
        state.createDeliveryAddressLoading = "rejected";
        state.error =
          action.error.message || "Failed to create delivery address";
      })

      // Shipping Methods
      .addCase(fetchShippingMethods.pending, (state) => {
        state.shippingMethodsLoading = "pending";
        state.error = null;
      })
      .addCase(fetchShippingMethods.fulfilled, (state, action) => {
        const shipment = action.payload[0];
        if (!shipment) {
          state.shippingMethods = null;
          return;
        }

        state.shippingMethods = {
          shipmentId: shipment.id,
          methods: shipment.available_shipping_methods.map(
            (method: ShippingMethod) => ({
              id: method.id,
              name: method.name,
              price: method.price,
            })
          ),
        };
        state.shippingMethodsLoading = "fulfilled";
      })
      .addCase(fetchShippingMethods.rejected, (state, action) => {
        state.shippingMethodsLoading = "rejected";
        state.error =
          action.error.message || "Failed to fetch shipping methods";
      })

      // Select Delivery Method
      .addCase(fetchSelectDeliveryMethod.pending, (state) => {
        state.selectDeliveryMethodLoading = "pending";
        state.error = null;
      })
      .addCase(fetchSelectDeliveryMethod.fulfilled, (state, action) => {
        state.selectDeliveryMethodLoading = "fulfilled";
        state.selectDeliveryMethodResponse = action.payload;
      })
      .addCase(fetchSelectDeliveryMethod.rejected, (state, action) => {
        state.selectDeliveryMethodLoading = "rejected";
        state.error =
          action.error.message || "Failed to select delivery method";
      });
  },
});

export const {
  setDeliveryAddress,
  setSelectedDeliveryMethod,
  setSelectedServicePoint,
  setSelectedParcelBox,
  resetDeliveryState,
} = deliverySlice.actions;

export default deliverySlice.reducer;
